import { formatFloat, ProjectFile, ProjectFileError, type ProjectElement } from '../../core/hp/projectFile';
import type { RtEvent } from './wire';

export type PeerDivergence = { key: string; reason: string };
/** What a batch did to the stored file: how many events landed, which did not and why, and ids the file lacks. */
export type PeerEventResult = { applied: number; divergent: PeerDivergence[]; unknownIds: string[] };

/** The app's `Furniture` and its subclasses (`Opening`, `Wall`, `Plinth`); `DomainChangeReceiver` tests `target is Furniture`. */
const FURNITURE = new Set(['Furniture', 'Opening', 'Wall', 'Plinth', 'CeiliningMolding']);
const TRIM = new Set(['Plinth', 'CeiliningMolding']);
const SIDES = ['Front', 'Back', 'Left', 'Right'];
/** `AbstractPlain`: a wall side, a floor, a ceiling. */
const PLAIN = new Set([...SIDES, 'Floor', 'Ceiling']);
const CONTAINERS: Readonly<Record<string, string>> = { Furniture: 'FurnitureCollection', Opening: 'Openings', Wall: 'Walls', Room: 'Rooms', Level: 'Levels' };
const FLOAT = /^\s*[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?\s*$/;
/** What `int.TryParse(…, NumberStyles.Integer)` takes: spaces around, a sign, digits. */
const INTEGER = /^\s*[+-]?\d+\s*$/;
/** Where a moved wall's old end and a room corner count as one point (the AI designer's applier). */
const CORNER = 0.02;

class Divergent extends Error {
  constructor(readonly reason: string) { super(reason); }
}

/** `replay`: the batch is rt's replay to a newcomer, its log since the base in order, which can repeat edits the base already holds. */
type Context = { replay: boolean };
/** `partial`: it landed, but the app writes more than the file can follow; `unchanged`: the file already reads as sent. */
type Change = (file: ProjectFile, element: ProjectElement, value: string, context: Context) => void | 'partial' | 'unchanged';
/** One property: the tags the app applies it to, and how it lands in the file the way the app's serializer writes it. */
type Rule = { on: (tag: string) => boolean; apply: Change };

const furniture = (tag: string) => FURNITURE.has(tag);
const only = (...tags: string[]) => (tag: string) => tags.includes(tag);

function float(value: string): number {
  if (!FLOAT.test(value)) throw new Divergent('malformed');
  return Number(value);
}

function json(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
  } catch { /* the same as a value of the wrong shape */ }
  throw new Divergent('malformed');
}

const coordinate = (vector: unknown, axis: string): number => {
  const value = (vector as Record<string, unknown> | null)?.[axis];
  return typeof value === 'number' ? value : value === undefined ? 0 : NaN;
};

const numberAt = (element: ProjectElement | undefined, name: string) => Number(element?.attribute(name) ?? 0) || 0;

/** `SerializeProperty(…, skipZero: true)`: a zero is not written at all. */
function setNumber(file: ProjectFile, element: ProjectElement, name: string, value: number, skipZero: boolean): void {
  if (skipZero && Math.fround(value) === 0) file.setText(element, name, null);
  else file.setNumber(element, name, value);
}

/** The app parses only `"True"` as true (`ParseBool`) and writes `bool.ToString()`; a file that spells it in lower case keeps that. */
function setBool(file: ProjectFile, element: ProjectElement, name: string, value: string, skipFalse: boolean): void {
  const on = value === 'True', current = element.attribute(name);
  if (!on && skipFalse) { file.setText(element, name, null); return; }
  const lower = current === 'true' || current === 'false';
  file.setText(element, name, lower ? String(on) : on ? 'True' : 'False');
}

const number = (name: string, skipZero = true): Change => (file, element, value) => setNumber(file, element, name, float(value), skipZero);
const flag = (name: string, skipFalse = true): Change => (file, element, value) => setBool(file, element, name, value, skipFalse);
const text = (name: string): Change => (file, element, value) => file.setText(element, name, value);

/** `Material`, `View`, `SlopesMaterial`: the codec's `<Material>` under the property's own tag; `cleared` takes it off. */
const material = (tag: string): Change => (file, element, value) => {
  const parsed = json(value);
  if (parsed.cleared === true) { file.replaceChild(element, tag, null); return; }
  const xml = typeof parsed.materialXml === 'string' ? parsed.materialXml.trim() : '';
  if (!xml) throw new Divergent('not-modelled');
  const root = /^<([^\s/>]+)/.exec(xml)?.[1];
  if (!root) throw new Divergent('malformed');
  const retagged = root === tag ? xml : `<${tag}${xml.slice(root.length + 1).replace(new RegExp(`</${root}>$`), `</${tag}>`)}`;
  file.replaceChild(element, tag, retagged);
};

/** A wall's sides take its height, and the front and back its thickness (`Wall.UpdateWallSize`). */
function sides(file: ProjectFile, wall: ProjectElement, tags: string[], name: string, value: number): void {
  for (const tag of tags) for (const side of file.children(wall, tag)) file.setNumber(side, name, value);
}

/** Ends as `Wall.SetStartEnd` keeps them (Start is the +x end at Rotation 0); the floor corners at the old ends follow, which the app never sends. */
const endpoints: Change = (file, wall, value) => {
  const parsed = json(value);
  const [sx, sy, ex, ey] = [coordinate(parsed.start, 'x'), coordinate(parsed.start, 'y'), coordinate(parsed.end, 'x'), coordinate(parsed.end, 'y')];
  if (![sx, sy, ex, ey].every(Number.isFinite) || Math.hypot(ex - sx, ey - sy) < 1e-4) throw new Divergent('malformed');
  const position = file.children(wall, 'Position')[0];
  const x = numberAt(position, 'X'), y = numberAt(position, 'Y');
  const half = numberAt(wall, 'Width') / 2, turn = numberAt(wall, 'Rotation') * Math.PI / 180;
  const old = [[x + half * Math.cos(turn), y - half * Math.sin(turn)], [x - half * Math.cos(turn), y + half * Math.sin(turn)]];
  let angle = -Math.atan2(-(ey - sy), -(ex - sx)) * 180 / Math.PI;
  if (angle < 0) angle += 360;
  file.setPosition(wall, (sx + ex) / 2, (sy + ey) / 2);
  file.setNumber(wall, 'Width', Math.hypot(ex - sx, ey - sy));
  setNumber(file, wall, 'Rotation', angle, true);
  const walls = file.parent(wall), room = walls && file.parent(walls);
  const floor = room && file.children(room, 'Floor')[0], shape = floor && file.children(floor, 'Shape')[0];
  for (const vertex of shape ? file.children(shape, 'Vector2') : []) {
    const here = [numberAt(vertex, 'x'), numberAt(vertex, 'y')];
    [[sx, sy], [ex, ey]].forEach((now, end) => {
      if (Math.hypot(here[0] - old[end][0], here[1] - old[end][1]) >= CORNER) return;
      file.setNumber(vertex, 'x', now[0]);
      file.setNumber(vertex, 'y', now[1]);
    });
  }
};

/** `FurnitureVariation.SerializeToString` stamps each variation it sends with `Revision`, which the app's save never writes. */
const withoutRevision = (xml: string) => xml.replace(/<[^\s!?/][^<>]*>/g, tag => tag.replace(/\s+Revision="[^"]*"/g, ''));

/**
 * A whole `<Info>` for a refinished piece, else `FurnitureId@Id` of the variation it was swapped for: the app builds a new
 * piece from the catalog and carries over only its place, turn, lock and Data (`ProjectHelper.PropertiesTransition`), its
 * Scale and its own Material. So the old part finishes, the note and the hidden shadow go, the piece is no catalog or PRO
 * add; its caption, price and size stay the file's. Partial: a whole Info, a swap to another model, whose size the app
 * takes from that model (`FurnitureVariation`), and a light of the piece's own, which the catalog's replaces.
 */
const variation: Change = (file, element, value) => {
  const info = value.replace(/^material_/, '');
  if (info.startsWith('<Info')) { file.replaceChild(element, 'Info', withoutRevision(info)); return 'partial'; }
  const at = value.indexOf('@'), furnitureId = at < 0 ? value : value.slice(0, at), id = at < 0 ? '' : value.slice(at + 1);
  const current = file.children(element, 'Info')[0];
  if (!current || !furnitureId) throw new Divergent('not-modelled');
  const otherModel = current.attribute('FurnitureId') !== furnitureId;
  file.setText(current, 'FurnitureId', furnitureId);
  if (!id) return 'partial';
  file.setText(current, 'Id', id);
  for (const materials of file.children(current, 'Materials')) file.remove(materials);
  for (const name of ['AddedFromCatalog', 'AddedAsPro']) if (element.attribute(name) !== undefined) setBool(file, element, name, 'False', false);
  file.setText(element, 'HideShadow', null);
  file.replaceChild(element, 'Note', null);
  return otherModel || file.children(element, 'Light').length ? 'partial' : undefined;
};

/**
 * `DomainChangeReceiver.ApplyLevelOrder`; an index past the levels throws from `move`. rt's replay can repeat a move its
 * base already holds, and a move made twice can rebuild a wrong order: it lands as partial.
 */
const levelOrder: Change = (file, level, value, { replay }) => {
  if (!INTEGER.test(value)) throw new Divergent('malformed');
  if (!file.move(level, Number(value))) return 'unchanged';
  return replay ? 'partial' : undefined;
};

/**
 * `OutsideHistorySync`: the estimate and the moodboard ride the Building as the whole node the file keeps under `<Project>`,
 * re-sent after every join; the node it holds byte for byte is no edit, any other one the web does not write.
 */
const projectNode = (tag: string): Change => (file, _building, value) => {
  const held = file.children(file.element([]), tag)[0];
  if (held && file.xmlOf(held) === value) return 'unchanged';
  throw new Divergent('not-modelled');
};

/** `DomainLifecycleHandler.AddWall`: a catalog partition comes with its room's floor as parent and joins that room's walls; another parent but a level sends it to whichever floor the receiving app has open. */
function wallHost(file: ProjectFile, parent: ProjectElement): ProjectElement {
  if (parent.tag === 'Level') return parent;
  const room = parent.tag === 'Floor' ? file.parent(parent) : null;
  if (room?.tag === 'Room') return room;
  throw new Divergent('not-modelled');
}

/**
 * `DomainChangeReceiver.ApplyChange`: a property the app ignores on a tag is ignored here too; one it applies in a way the
 * file cannot follow, and one not listed, are recorded as not modelled.
 */
const RULES: Readonly<Record<string, Rule>> = {
  Width: { on: furniture, apply: number('Width') },
  Depth: { on: furniture, apply: (file, element, value) => {
    const depth = float(value);
    setNumber(file, element, 'Depth', depth, true);
    if (element.tag === 'Wall') sides(file, element, ['Front', 'Back'], 'Thickness', depth);
  } },
  Height: { on: tag => furniture(tag) || tag === 'Ceiling', apply: (file, element, value) => {
    let height = float(value);
    if (element.tag === 'Ceiling') { file.setNumber(element, 'Height', height); return; }
    if (element.tag === 'Wall') {
      if (!(height >= 0.01)) throw new Divergent('malformed');
      height = Math.round(height * 1000) / 1000;
      sides(file, element, SIDES, 'Height', height);
    }
    setNumber(file, element, 'Height', height, true);
  } },
  Rotation: { on: furniture, apply: number('Rotation') },
  Tilt: { on: furniture, apply: number('Tilt') },
  PlaneOffset: { on: furniture, apply: number('PlaneOffset') },
  Locked: { on: furniture, apply: (file, element, value) => {
    // Wall.SerializeInner saves a wall's lock as LockedSize too, ahead of its base's Locked.
    if (element.tag === 'Wall') setBool(file, element, 'LockedSize', value, true);
    setBool(file, element, 'Locked', value, true);
  } },
  SizeLocked: { on: only('Opening'), apply: flag('SizeLocked') },
  LockedLeft: { on: only('Opening'), apply: flag('LockedLeft') },
  LockedRight: { on: only('Opening'), apply: flag('LockedRight') },
  IsHidden: { on: tag => tag === 'Wall' || TRIM.has(tag), apply: (file, element, value) => {
    if (element.tag !== 'Wall') { setBool(file, element, 'IsHidden', value, true); return; }
    const own = file.children(element, 'wall')[0];
    if (own) setBool(file, own, 'IsHidden', value, false);
    else file.replaceChild(element, 'wall', `<wall IsHidden="${value === 'True' ? 'True' : 'False'}" />`);
  } },
  Data: { on: furniture, apply: text('Data') },
  Position: { on: furniture, apply: (file, element, value) => {
    const point = json(value);
    file.setPosition(element, coordinate(point, 'x'), coordinate(point, 'y'));
  } },
  Scale: { on: furniture, apply: (file, element, value) => {
    const scale = json(value), [x, y, z] = ['x', 'y', 'z'].map(axis => coordinate(scale, axis));
    if (![x, y, z].every(Number.isFinite)) throw new Divergent('malformed');
    const current = file.children(element, 'Scale')[0];
    // The app writes a Scale only when it is not (1, 1, 1).
    if (x === 1 && y === 1 && z === 1) file.replaceChild(element, 'Scale', null);
    else if (current) { file.setNumber(current, 'x', x); file.setNumber(current, 'y', y); file.setNumber(current, 'z', z); }
    else file.replaceChild(element, 'Scale', `<Scale x="${formatFloat(x)}" y="${formatFloat(y)}" z="${formatFloat(z)}" />`);
  } },
  Endpoints: { on: only('Wall'), apply: endpoints },
  Material: { on: tag => PLAIN.has(tag) || furniture(tag), apply: (file, element, value, context) => {
    // Furniture.SetMaterial paints the item itself, a modular part or a new variation, by catalog data the file lacks.
    if (!PLAIN.has(element.tag) && !TRIM.has(element.tag)) throw new Divergent('not-modelled');
    material('Material')(file, element, value, context);
  } },
  View: { on: tag => tag === 'Opening' || SIDES.includes(tag), apply: material('View') },
  SlopesMaterial: { on: only('Opening'), apply: material('SlopesMaterial') },
  Variation: { on: furniture, apply: variation },
  LevelName: { on: only('Level'), apply: text('LevelName') },
  IsLowerLevel: { on: only('Level'), apply: flag('IsLowerLevel', false) },
  LevelOrder: { on: only('Level'), apply: levelOrder },
  RoomName: { on: only('Room'), apply: text('RoomName') },
  RoomType: { on: only('Room'), apply: text('RoomType') },
  ClientEstimate: { on: only('Building'), apply: projectNode('ClientEstimate') },
  Moodboard: { on: only('Building'), apply: projectNode('Moodboard') },
};

/**
 * The app's committed edits, point-wise by UniqueId into the stored file; one that cannot land is recorded and never stops
 * its batch. `observe` is told of each event just before it is tried, the file as the events before it left it. In a file
 * with every id, an object the batch takes out is no loss when absent: the room no longer holds it (rt's replay can repeat
 * a remove its saved base already holds, persist.js).
 */
export function applyPeerEvents(file: ProjectFile, events: readonly RtEvent[], observe?: (event: RtEvent, key: string) => void,
  context: Context = { replay: false }): PeerEventResult {
  const result: PeerEventResult = { applied: 0, divergent: [], unknownIds: [] };
  const unknown = new Set<string>(), gone = goneIn(events);
  let lenient: boolean | undefined;
  const settled = (id: string) => gone.has(id) && (lenient ??= !file.idless);
  const miss = (key: string, id: string) => { unknown.add(id); result.divergent.push({ key, reason: 'unknown-id' }); };
  let deferred: RtEvent[] = [];
  const run = (event: RtEvent, last: boolean): boolean => {
    const key = typeof event.key === 'string' ? event.key : '';
    try {
      observe?.(event, key);
      const outcome = applyOne(file, event, key, context);
      if (outcome === 'parent-missing') {
        if (!last) return false;
        const parentId = String((event.data as { parentId?: unknown } | undefined)?.parentId ?? '');
        if (!settled(key) && !settled(parentId)) miss(key, parentId);
      } else if (outcome === 'unknown') {
        const target = targetOf(event, key);
        if (!settled(target)) miss(key, target);
      } else if (outcome !== 'unchanged') {
        result.applied++;
        if (outcome === 'partial') result.divergent.push({ key, reason: 'partial' });
      }
    } catch (error) {
      result.divergent.push({ key, reason: error instanceof Divergent ? error.reason : error instanceof ProjectFileError ? error.code : 'malformed' });
    }
    return true;
  };
  for (const event of events) if (!run(event, false)) deferred.push(event);
  // An add whose parent arrives later in the same batch lands once it does (the app would drop it).
  for (let progress = true; deferred.length && progress;) {
    const before = deferred.length;
    deferred = deferred.filter(event => !run(event, false));
    progress = deferred.length < before;
  }
  for (const event of deferred) run(event, true);
  result.unknownIds = [...unknown];
  return result;
}

const targetOf = (event: RtEvent, key: string) => event.type === 'DomainChange' ? key.split('::')[0] : key;

/** rt's replay can repeat an add over a base that already holds a later carry of the piece: a held piece on another host. */
function carried(file: ProjectFile, key: string, host: ProjectElement): boolean {
  const container = file.parent(file.byId(key)!);
  return !!container && file.parent(container) !== host;
}

const lifecycle = (event: RtEvent, op: string) => event.type === 'DomainLifecycle' && typeof event.key === 'string' && !!event.key
  && (event.data as { op?: unknown } | undefined)?.op === op;

/** The keys the batch leaves removed (its last add or remove of them a remove), and every add hung under one, to the end of the chain. */
function goneIn(events: readonly RtEvent[]): Set<string> {
  const removedLast = new Map<string, boolean>();
  for (const event of events) if (lifecycle(event, 'add') || lifecycle(event, 'remove')) removedLast.set(event.key as string, lifecycle(event, 'remove'));
  const gone = new Set([...removedLast].filter(([, removed]) => removed).map(([key]) => key));
  const adds = gone.size ? events.filter(event => lifecycle(event, 'add')) : [];
  for (let grew = true; grew;) {
    grew = false;
    for (const event of adds) {
      if (gone.has(event.key as string) || !gone.has(String((event.data as { parentId?: unknown }).parentId ?? ''))) continue;
      gone.add(event.key as string);
      grew = true;
    }
  }
  return gone;
}

function applyOne(file: ProjectFile, event: RtEvent, key: string, context: Context): 'applied' | 'partial' | 'unchanged' | 'unknown' | 'parent-missing' {
  const data = (event.data && typeof event.data === 'object' ? event.data : {}) as Record<string, unknown>;
  if (event.type === 'DomainLifecycle') {
    if (data.op === 'remove') {
      const element = file.byId(key);
      if (!element) return 'unknown';
      file.remove(element);
      return 'applied';
    }
    if (data.op !== 'add') throw new Divergent('not-modelled');
    const kind = typeof data.kind === 'string' ? data.kind : 'Furniture', container = CONTAINERS[kind];
    if (!container) throw new Divergent('not-modelled');
    const parent = kind === 'Level' ? file.element([['Building', 0]]) : file.byId(String(data.parentId ?? ''));
    if (!parent) return 'parent-missing';
    const host = kind === 'Wall' ? wallHost(file, parent) : parent;
    const added = file.insertFromApp(host, container, String(data.xml ?? ''), key);
    if (!added) return context.replay && carried(file, key, host) ? 'partial' : 'unchanged';
    // DomainLifecycleHandler.AddLevel puts a lower level first (`bool.TryParse`).
    if (kind === 'Level' && /^\s*true\s*$/i.test(added.attribute('IsLowerLevel') ?? '')) file.move(added, 0);
    return 'applied';
  }
  if (event.type !== 'DomainChange') return 'unchanged';
  const split = key.indexOf('::');
  if (split < 0) throw new Divergent('malformed');
  const rule = RULES[key.slice(split + 2)];
  if (!rule) throw new Divergent('not-modelled');
  const element = file.byId(key.slice(0, split));
  if (!element) return 'unknown';
  if (!rule.on(element.tag)) return 'unchanged';
  if (typeof data.value !== 'string') throw new Divergent('malformed');
  return rule.apply(file, element, data.value, context) ?? 'applied';
}
